import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "classnames";
import { useStyles } from "./TVKeyboard.style";
import {
  HintChip,
  KeyDef,
  lettersLayout,
  symbolsLayout,
} from "./keyboardLayout";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { Icon } from "../../core/Icon/Icon";
import CircleIcon from "../../../assets/icons/Circle.svg";
import SquareIcon from "../../../assets/icons/Square.svg";
import TriangleIcon from "../../../assets/icons/Triangle.svg";

const SONY_HINT_ICON: Partial<Record<HintChip, string>> = {
  B: CircleIcon,
  X: SquareIcon,
  Y: TriangleIcon,
};

const SONY_HINT_LABEL: Partial<Record<HintChip, string>> = {
  LB: "L1",
  LT: "L2",
  RB: "R1",
  RT: "R2",
};

const BUTTON_ICON: Partial<Record<string, string>> = {
  shift: "icon-bold-arrow-up",
  cursorLeft: "icon-linear-arrow-left-3",
  cursorRight: "icon-linear-arrow-right-3",
  backspace: "icon-bold-delete",
};

interface ITVKeyboardProps {
  open: boolean;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  onClose?: () => void;
  // Optional ref to the underlying input so the keyboard can mirror its cursor.
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onCursorChange?: (cursor: number) => void;
  onSendKey?: (key: string) => void;
}

const KEY_DATA_GAMEPAD_PREFIX = "tvkb-";

export const TVKeyboard: FC<ITVKeyboardProps> = ({
  open,
  value,
  onChange,
  onSubmit,
  onClose,
  inputRef,
  onCursorChange,
  onSendKey,
}) => {
  const classes = useStyles();
  const { gamepadEmitter, gamepads, setActiveElement } = useGamepadContext();
  const gamepadType = gamepads?.find((gp) => gp.type !== "generic")?.type;
  const containerRef = useRef<HTMLDivElement>(null);
  const [layoutMode, setLayoutMode] = useState<"letters" | "symbols">(
    "letters",
  );
  const [shift, setShift] = useState(false);
  const [cursor, setCursor] = useState(value.length);

  // Keep cursor in range when value changes externally.
  useEffect(() => {
    setCursor((c) => Math.min(Math.max(0, c), value.length));
  }, [value.length]);

  // Reset cursor on open
  useEffect(() => {
    if (open) setCursor(value.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    onCursorChange?.(cursor);
  }, [cursor, onCursorChange]);

  const layout = layoutMode === "letters" ? lettersLayout : symbolsLayout;

  const setUnderlyingCursor = useCallback(
    (pos: number) => {
      const input = inputRef?.current;
      if (input) {
        try {
          input.setSelectionRange(pos, pos);
        } catch {
          // Some input types (search) don't support selection — ignore.
        }
      }
    },
    [inputRef],
  );

  const insertText = useCallback(
    (text: string) => {
      if (onSendKey) {
        onSendKey(text);
        return;
      }
      const next = value.slice(0, cursor) + text + value.slice(cursor);
      const nextCursor = cursor + text.length;
      onChange(next);
      setCursor(nextCursor);
      setUnderlyingCursor(nextCursor);
    },
    [cursor, onChange, onSendKey, setUnderlyingCursor, value],
  );

  const backspace = useCallback(() => {
    if (onSendKey) {
      onSendKey("Backspace");
      return;
    }
    if (cursor === 0) return;
    const next = value.slice(0, cursor - 1) + value.slice(cursor);
    const nextCursor = cursor - 1;
    onChange(next);
    setCursor(nextCursor);
    setUnderlyingCursor(nextCursor);
  }, [cursor, onChange, onSendKey, setUnderlyingCursor, value]);

  const moveCursor = useCallback(
    (delta: number) => {
      if (onSendKey) {
        onSendKey(delta > 0 ? "ArrowRight" : "ArrowLeft");
        return;
      }
      const next = Math.min(Math.max(0, cursor + delta), value.length);
      setCursor(next);
      setUnderlyingCursor(next);
    },
    [cursor, onSendKey, setUnderlyingCursor, value.length],
  );

  const onSubmitClick = useCallback(() => {
    if (onSendKey) onSendKey("Enter");
    else onSubmit?.(value);
    onClose?.();
  }, [onClose, onSendKey, onSubmit, value]);

  const repositionActiveByIndex = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const keys = Array.from(
      container.querySelectorAll<HTMLElement>("[data-gamepad]"),
    );
    const activeIndex = keys.findIndex((el) =>
      el.classList.contains(classes.selected),
    );
    if (activeIndex < 0) return;
    requestAnimationFrame(() => {
      const nextKey =
        containerRef.current?.querySelectorAll<HTMLElement>("[data-gamepad]")[
          activeIndex
        ];
      if (nextKey) setActiveElement(nextKey);
    });
  }, [classes.selected, setActiveElement]);

  const pressKey = useCallback(
    (key: KeyDef) => {
      switch (key.type) {
        case "char": {
          const v = shift
            ? key.shiftValue ?? key.shiftLabel ?? key.value ?? key.label
            : key.value ?? key.label;
          insertText(v);
          break;
        }
        case "space":
          insertText(" ");
          break;
        case "backspace":
          backspace();
          break;
        case "cursorLeft":
          moveCursor(-1);
          break;
        case "cursorRight":
          moveCursor(1);
          break;
        case "shift":
          repositionActiveByIndex();
          setShift((s) => !s);
          break;
        case "symbols":
          repositionActiveByIndex();
          setLayoutMode((m) => (m === "letters" ? "symbols" : "letters"));
          break;
        case "enter":
          onSubmitClick();
          break;
        case "hide":
          onClose?.();
          break;
      }
    },
    [
      insertText,
      backspace,
      moveCursor,
      onSubmitClick,
      onClose,
      shift,
      repositionActiveByIndex,
    ],
  );

  // Listen for gamepad events
  useEffect(() => {
    if (!open) return;
    const ACTION_TO_KEY_ID: Record<string, string> = {
      "x-square": "backspace",
      "y-triangle": "space",
      "lb-l1": "shift",
      "rb-r1": "symbols",
      "rt-r2": "enter",
    };
    const handleAction = (action: string) => {
      const keyId = ACTION_TO_KEY_ID[action];
      if (!keyId) return;
      const key = layout.find((k) => k.id === keyId);
      if (key) pressKey(key);
    };
    gamepadEmitter.on("action", handleAction);
    return () => {
      gamepadEmitter.off("action", handleAction);
    };
  }, [gamepadEmitter, open, layout, pressKey]);

  // Render order has to follow row-major top-to-bottom so the gamepad
  // provider's onDefaultMoveAction (which uses bounding rects) picks
  // sensible neighbors.
  const orderedKeys = useMemo(
    () => [...layout].sort((a, b) => a.row - b.row || a.col - b.col),
    [layout],
  );

  if (!open) return null;

  const content = (
    <div ref={containerRef} className={classes.root} id="keyboard-container">
      <div className={classes.grid}>
        {orderedKeys.map((key) => {
          const span = key.colSpan ?? 1;
          const rowSpan = key.rowSpan ?? 1;
          const isToggled =
            (key.type === "shift" && shift) ||
            (key.type === "symbols" && layoutMode === "symbols");
          const display =
            key.type === "char" && shift
              ? key.shiftLabel ?? key.label
              : key.label;
          return (
            <button
              key={key.id}
              type="button"
              data-gamepad={`${KEY_DATA_GAMEPAD_PREFIX}${key.id}`}
              data-selected-class={classes.selected}
              data-gamepad-no-auto-scroll="true"
              className={clsx(classes.key, {
                [classes.keyActive]: isToggled,
              })}
              style={{
                gridColumn: `${key.col} / span ${span}`,
                gridRow: `${key.row} / span ${rowSpan}`,
              }}
              onClick={() => pressKey(key)}
            >
              {BUTTON_ICON[key.type] ? (
                <Icon name={BUTTON_ICON[key.type]} size={35} />
              ) : (
                display
              )}
              {key.hint && gamepadType && (
                <span className={classes.hintChip}>
                  {gamepadType === "sony" && SONY_HINT_ICON[key.hint] ? (
                    <img
                      src={SONY_HINT_ICON[key.hint]}
                      alt={key.hint}
                      className={classes.hintIcon}
                    />
                  ) : gamepadType === "sony" && SONY_HINT_LABEL[key.hint] ? (
                    SONY_HINT_LABEL[key.hint]
                  ) : (
                    key.hint
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
